import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/Auth0Context'
import { UserRole } from './types/user'
import { Loader2 } from 'lucide-react'

// Pages
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import CallbackPage from './pages/auth/CallbackPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import ResetPasswordPage from './pages/auth/ResetPasswordPage'
import OnboardingPage from './pages/onboarding/OnboardingPage'
import MentorDashboard from './pages/mentor/MentorDashboard'
import MenteeDashboard from './pages/mentee/MenteeDashboard'
import MessagesPage from './pages/messages/MessagesPage'
import TasksPage from './pages/tasks/TasksPage'
import MeetingsPage from './pages/meetings/MeetingsPage'
import MeetingDetailView from './pages/meetings/MeetingDetailView'
import MeetingSeriesView from './pages/meetings/MeetingSeriesView'
import ProfilePage from './pages/profile/ProfilePage'
import UserProfilePage from './pages/profile/UserProfilePage'
import FindMentorsPage from './pages/matching/FindMentorsPage'
import MenteesPage from './pages/matching/MenteesPage'
import FeedPage from './pages/feed/FeedPage'
import PostDetailPage from './pages/feed/PostDetailPage'
import NewsPage from './pages/news/NewsPage'
import PlansPage from './pages/plans/PlansPage'
import PlanDetailView from './pages/plans/PlanDetailView'
import ResourcesPage from './pages/resources/ResourcesPage'
import AdminLayout from './pages/admin/AdminLayout'
import AdminUsersPage from './pages/admin/AdminUsersPage'
import AdminUserDetailPage from './pages/admin/AdminUserDetailPage'
import AdminRelationshipsPage from './pages/admin/AdminRelationshipsPage'
import AdminReportsPage from './pages/admin/AdminReportsPage'
import AdminCuratedSourcesPage from './pages/admin/AdminCuratedSourcesPage'

// Components
import Navigation from './components/layout/Navigation'
import ProtectedRoute from './components/auth/ProtectedRoute'
import { FeedSocketProvider } from './contexts/FeedSocketContext'
import { MessagingSocketProvider } from './contexts/MessagingSocketContext'

const FEED_ENABLED = import.meta.env.VITE_ENABLE_FEED === 'true'

function App() {
  const { isLoading, isAuthenticated, user } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary-600" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  const content = (
    <div className="min-h-screen bg-gray-50">
      {isAuthenticated && user?.role !== UserRole.ADMIN && <Navigation />}

      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            isAuthenticated ?
              <Navigate to={getDashboardRoute(user?.role, user?.isOnboardingComplete)} replace /> :
              <LoginPage />
          }
        />
        <Route
          path="/register"
          element={
            isAuthenticated ?
              <Navigate to={getDashboardRoute(user?.role, user?.isOnboardingComplete)} replace /> :
              <RegisterPage />
          }
        />
        {/* Auth0 callback route */}
        <Route path="/auth/callback" element={<CallbackPage />} />

        {/* Onboarding route */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />

        {/* Deprecated password management routes - Auth0 handles these */}
        <Route
          path="/forgot-password"
          element={
            isAuthenticated ?
              <Navigate to={getDashboardRoute(user?.role, user?.isOnboardingComplete)} replace /> :
              <ForgotPasswordPage />
          }
        />
        <Route
          path="/reset-password"
          element={
            isAuthenticated ?
              <Navigate to={getDashboardRoute(user?.role, user?.isOnboardingComplete)} replace /> :
              <ResetPasswordPage />
          }
        />

        {/* Protected routes */}
        <Route
          path="/mentor/dashboard"
          element={
            <ProtectedRoute allowedRoles={[UserRole.MENTOR]}>
              <MentorDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mentee/dashboard"
          element={
            <ProtectedRoute allowedRoles={[UserRole.MENTEE]}>
              <MenteeDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/messages/*"
          element={
            <ProtectedRoute>
              <MessagesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks/*"
          element={
            <ProtectedRoute>
              <TasksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/meetings/:meetingId/series"
          element={
            <ProtectedRoute>
              <MeetingSeriesView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/meetings/:meetingId"
          element={
            <ProtectedRoute>
              <MeetingDetailView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/meetings/*"
          element={
            <ProtectedRoute>
              <MeetingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/plans/relationship/:relationshipId"
          element={
            <ProtectedRoute>
              <PlansPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/plans/:planId"
          element={
            <ProtectedRoute>
              <PlanDetailView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/resources"
          element={
            <ProtectedRoute>
              <ResourcesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users/:userId"
          element={
            <ProtectedRoute>
              <UserProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/find-mentors"
          element={
            <ProtectedRoute allowedRoles={[UserRole.MENTEE]}>
              <FindMentorsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mentees"
          element={
            <ProtectedRoute allowedRoles={[UserRole.MENTOR]}>
              <MenteesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/admin/users" replace />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="users/:id" element={<AdminUserDetailPage />} />
          <Route path="relationships" element={<AdminRelationshipsPage />} />
          <Route path="reports" element={<AdminReportsPage />} />
          <Route path="curated-sources" element={<AdminCuratedSourcesPage />} />
        </Route>

        {FEED_ENABLED && (
          <Route
            path="/feed"
            element={
              <ProtectedRoute>
                <FeedPage />
              </ProtectedRoute>
            }
          />
        )}
        {FEED_ENABLED && (
          <Route
            path="/feed/posts/:postId"
            element={
              <ProtectedRoute>
                <PostDetailPage />
              </ProtectedRoute>
            }
          />
        )}

        <Route
          path="/news"
          element={
            <ProtectedRoute>
              <NewsPage />
            </ProtectedRoute>
          }
        />

        {/* Default redirects */}
        <Route
          path="/"
          element={
            <Navigate
              to={isAuthenticated ? getDashboardRoute(user?.role, user?.isOnboardingComplete) : '/login'}
              replace
            />
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  )

  const withMessaging = isAuthenticated ? (
    <MessagingSocketProvider>{content}</MessagingSocketProvider>
  ) : (
    content
  )

  if (FEED_ENABLED && isAuthenticated) {
    return <FeedSocketProvider>{withMessaging}</FeedSocketProvider>
  }
  return withMessaging
}

function getDashboardRoute(role?: string, isOnboardingComplete?: boolean): string {
  // Redirect to onboarding if not complete
  if (isOnboardingComplete === false) {
    return '/onboarding'
  }

  switch (role) {
    case UserRole.ADMIN:
      return '/admin'
    case UserRole.MENTOR:
      return '/mentor/dashboard'
    case UserRole.MENTEE:
      return '/mentee/dashboard'
    default:
      return '/login'
  }
}

export default App